import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Input, Button } from '@cfm-media/pwa-ui-components';
import { LoadableStatus } from '../mobx-utils';
import { useStore } from '../core/store';

import { HTMLContent } from '../components/html-content';
import { LoadSpinner } from '../components/loader';
import { Modal } from '../components/modal';
import { VideoContent } from '../components/video-content';
import { isAuthenticated } from '../utils/auth';
import { urls } from '../urls';
import { useSignOnLink } from '../hooks/useSignOnLink';

const loginErrorMessages = {
  invalidCredentials:
    'Ungültige Anmeldedaten. Bitte überprüfen Sie Ihre Eingabe.',
  tooManyAttempts:
    'Aufgrund zu vieler Anmeldeversuche wurde ihr Zugang vorrübergehend gesperrt. Bitte versuchen Sie es später erneut.',
};

export const Login = observer(() => {
  const url = new URL(window.location.href);
  const redirectUrl = url.searchParams?.get('redirectUrl');

  const navigate = useNavigate();
  const signOnAttempt = useSignOnLink();
  const store = useStore();

  const [bookingId, setBookingId] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState('');

  async function onSubmit(event) {
    event.preventDefault();

    setIsLoading(true);
    setError(null);

    const { success, data, errorMessage } = await login({
      bookingId,
      token,
    });

    setIsLoading(false);

    if (success && data && !errorMessage) {
      store.BookingStore.booking.setData(data);
      store.BookingStore.booking.setStatus(LoadableStatus.done);
      if (redirectUrl) {
        navigate(urls[redirectUrl]);
      } else {
        navigate(urls.home);
      }
    } else {
      setError(errorMessage);
    }
  }

  useEffect(() => {
    if (isAuthenticated()) {
      if (redirectUrl) {
        navigate(urls[redirectUrl]);
        window.location.search = '';
      } else {
        navigate(urls.home);
      }
    }
  }, []);

  useEffect(() => {
    if (signOnAttempt.isLoading) {
      setIsLoading(signOnAttempt.isLoading);
    }
  }, [signOnAttempt.isLoading]);

  return (
    <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-[url('/public/assets/images/login-bg-small.jpg')] sm:bg-[url('/public/assets/images/login-bg.jpg')] bg-cover">
      <div className="px-6 sm:mx-auto sm:w-full sm:max-w-lg">
        <img
          className="mx-auto mb-10"
          width={188}
          height={48}
          src="/public/assets/images/logo.png"
          alt="Bentour Reisen"
        />
        <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
          Bitte melden Sie sich an
        </h2>
        <div className="bg-white p-8 mt-8 rounded-t-md shadow relative overflow-hidden">
          {isLoading ? (
            <div className="bg-brand-1/95 absolute inset-0 z-10">
              <div className="flex h-full justify-center items-center">
                <div className="mr-1">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <div className="text-white text-sm">
                  Sie werden sicher eingeloggt
                </div>
              </div>
            </div>
          ) : null}

          <form className="space-y-6" onSubmit={onSubmit}>
            <Input
              attribute="bookingId"
              id="username"
              autocomplete="username"
              label="Buchungsnummer"
              required
              type="username"
              value={bookingId}
              onChange={(event) => {
                setBookingId(event.target.value);
              }}
            />

            <Input
              attribute="token"
              label="Nachname oder MyBentour code"
              autocomplete="current-password"
              id="current-password"
              required
              type={token ? 'text' : 'password'}
              value={token}
              onChange={(event) => {
                setToken(event.target.value);
              }}
            />

            <LoginErrorMessage error={error} />

            <Button
              title="Anmelden"
              variant="primary"
              rounded="rounded-sm"
              block
            />
          </form>
        </div>

        <VideoContent
          videoId="vULLLcycJOA"
          thumbnailUrl="/public/assets/images/video-placeholder.png"
        />

        <HTMLContent />

        <div className="mt-11 flex justify-center">
          <Link
            to={urls.imprint}
            className="mx-3 text-md text-white hover:text-white/80 hover:underline hover:underline-offset-4 ease-out duration-200 "
          >
            Impressum
          </Link>

          <Link
            to={urls.dataProtection}
            className="mx-3 text-md text-white hover:text-white/80 hover:underline hover:underline-offset-4 ease-out duration-200 "
          >
            Datenschutz
          </Link>
        </div>
      </div>

      <SignOnLinkErrorModal
        setShow={signOnAttempt.setError}
        show={!!signOnAttempt.error}
        text={signOnAttempt.error}
        redirectUrl={redirectUrl}
      />

      <SignOnLinkSuccesModal
        setShow={signOnAttempt.setIsSuccessful}
        show={signOnAttempt.isSuccessful}
        redirect={redirectUrl}
        navigate={navigate}
      />
    </div>
  );
});

export async function login({ bookingId, token }) {
  let errorMessage = null;

  try {
    const response = await fetch('/login', {
      body: JSON.stringify({ bookingId, token }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });

    if (response.status === 429) {
      errorMessage = loginErrorMessages.tooManyAttempts;
      throw new Error('Too many failed login attempts');
    } else if (response.status !== 200) {
      errorMessage = loginErrorMessages.invalidCredentials;
      throw new Error('Invalid credentials');
    }

    const body = await response.json();

    return { success: true, data: body.data.booking, errorMessage: null };
  } catch (error) {
    console.error(error);

    return { success: false, data: null, errorMessage };
  }
}

const LoginErrorMessage = ({ error }) => {
  if (!error) return null;

  return (
    <div className="mt-2 text-sm text-red-600">
      <ExclamationCircleIcon
        className="h-5 w-5 text-red-500 mr-[7px] mt-[-1px] inline-flex"
        aria-hidden="true"
      />
      {error}
    </div>
  );
};

export const LoadSpinnerButton = ({ isLoading, label }) => (
  <button className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
    {isLoading ? <LoadSpinner /> : label}
  </button>
);

const SignOnLinkErrorModal = ({ show, setShow, text, redirectUrl }) => {
  const extendedErrorMessage =
    text === loginErrorMessages.tooManyAttempts
      ? 'Für diese Buchung wurden in der letzten Stunde zu viele Anmeldeversuche durchgeführt. Bitte versuchen Sie es später erneut.'
      : 'Die von Ihnen angegebene Buchungsnummer oder Nachname ist ungültig. Bitte überprüfen Sie Ihre Eingabe.';

  return (
    <Modal
      show={show}
      type="error"
      setShow={setShow}
      headline="Login fehlgeschlagen"
      text={extendedErrorMessage}
      buttonText="Weiter"
      fullWidth
      onClose={() => {
        if (redirectUrl) {
          window.location.search = `redirectUrl=${redirectUrl}`;
        } else {
          window.location.search = '';
        }
      }}
    />
  );
};

const SignOnLinkSuccesModal = ({ show, setShow, redirect, navigate }) => (
  <Modal
    show={show}
    type="success"
    setShow={setShow}
    headline="Login erfolgreich"
    text="Sie wurden erfolgreich angemeldet. Sie können jetzt Ihre Buchungen einsehen."
    buttonText="Weiter"
    fullWidth
    onClose={() => {
      redirect && redirect?.length
        ? navigate(urls[redirect])
        : navigate(urls.home);
    }}
  />
);
